import React, { useState } from "react";
import {
    FaFacebookF,
    FaInstagram,
    FaPhoneAlt,
    FaEnvelope,
    FaMapMarkerAlt,
    FaWhatsapp,
    FaTelegram,
} from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { useSettings } from "../hooks/useSettings";
import defaultLogo from "../assets/logo.jpg";

const Footer = () => {
    const currentYear = new Date().getFullYear();
    const [notification, setNotification] = useState(null);
    const { theme } = useSettings();
    const { t, i18n } = useTranslation();

    const isRTL = i18n.dir() === "rtl";

    const getNotificationText = (notif) => {
        if (!notif) return "";
        return typeof notif === "string" ? notif : notif.message;
    };

    const handleWhatsAppSubmit = async (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        const phoneNumber = formData.get("whatsappNumber");

        // Basic validation
        if (!phoneNumber || !/^\d{9,15}$/.test(phoneNumber)) {
            setNotification({
                type: "error",
                message: t("footer.subscribeError"),
            });
            return;
        }

        try {
            // Here we'll call the API to save the WhatsApp number
            // This will be implemented after we create the backend endpoint
            const response = await fetch("/api/whatsapp-subscribers", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Accept-Language": i18n.language,
                },
                body: JSON.stringify({ phoneNumber }),
            });

            if (!response.ok) {
                throw new Error("Failed to subscribe");
            }

            setNotification({
                type: "success",
                message: t("footer.subscribeSuccess"),
            });

            // Reset form
            e.target.reset();

            // Clear success message after 5 seconds
            setTimeout(() => {
                setNotification(null);
            }, 5000);
        } catch (error) {
            console.error("Error subscribing:", error);
            setNotification({
                type: "error",
                message: t("common.errorOccurred"),
            });
        }
    };

    const handlePolicyClick = (policy) => {
        let message = "";

        switch (policy) {
            case "Privacy Policy":
                message =
                    "📋 Privacy Policy - We protect your personal information and respect your privacy rights.";
                break;
            case "Terms of Service":
                message =
                    "📜 Terms of Service - Please review our terms and conditions for using our services.";
                break;
            case "Cookie Policy":
                message =
                    "🍪 Cookie Policy - We use cookies to enhance your browsing experience and analyze site traffic.";
                break;
            default:
                message = policy;
        }

        setNotification(message);
        setTimeout(() => setNotification(null), 4000);
    };

    return (
        <footer
            className="bg-safir-dark text-white relative"
            dir={isRTL ? "rtl" : "ltr"}
        >
            {/* Main Footer */}
            <div className="container-float px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
                <div
                    className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 lg:gap-10 xl:gap-14 items-start ${isRTL ? "text-right" : ""}`}
                >
                    {/* Column 1 - About */}
                    <div
                        className={`w-full flex flex-col ${isRTL ? "items-center text-right" : "items-center sm:items-start text-center sm:text-left"}`}
                    >
                        <div
                            className={`flex items-center mb-4 sm:mb-6 ${isRTL ? "flex-row-reverse" : ""}`}
                        >
                            <img
                                src={theme?.logo || defaultLogo}
                                alt="Safir the Elite"
                                className={`h-8 sm:h-10 object-contain ${isRTL ? "mr-4 sm:ml-3" : "mr-2 sm:mr-3"}`}
                            />
                            <h3 className="text-lg sm:text-xl font-bold">
                                {t("footer.about")}
                            </h3>
                        </div>
                        <p
                            className={`text-gray-400 mb-4 sm:mb-6 text-sm sm:text-base ${isRTL ? "text-right" : ""}`}
                        >
                            {t("footer.description")}
                        </p>
                        <div
                            className={`flex flex-wrap gap-2 sm:gap-3 ${isRTL ? "justify-end flex-row-reverse" : "justify-center sm:justify-start"}`}
                        >
                            <a
                                href="https://www.facebook.com/profile.php?id=61570072173224&mibextid=ZbWKwL"
                                target="_blank"
                                rel="noreferrer"
                                className="bg-safir-primary hover:bg-safir-secondary text-white w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center transition-colors duration-300"
                            >
                                <FaFacebookF className="text-sm sm:text-base" />
                            </a>
                            <a
                                href={`mailto:${t("footer.footerEmail")}`}
                                className="bg-safir-primary hover:bg-safir-secondary text-white w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center transition-colors duration-300"
                                rel="noreferrer"
                            >
                                <FaEnvelope className="text-sm sm:text-base" />
                            </a>
                            <a
                                href="https://www.instagram.com/safirtheelite/?hl=en"
                                target="_blank"
                                rel="noreferrer"
                                className="bg-safir-primary hover:bg-safir-secondary text-white w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center transition-colors duration-300"
                            >
                                <FaInstagram className="text-sm sm:text-base" />
                            </a>
                            <a
                                href="https://wa.me/+93782818883"
                                target="_blank"
                                rel="noreferrer"
                                className="bg-safir-primary hover:bg-safir-secondary text-white w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center transition-colors duration-300"
                            >
                                <FaWhatsapp className="text-sm sm:text-base" />
                            </a>
                            <a
                                href="https://t.me/+r94h7v1HBttmZmVl"
                                target="_blank"
                                rel="noreferrer"
                                className="bg-safir-primary hover:bg-safir-secondary text-white w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center transition-colors duration-300"
                            >
                                <FaTelegram className="text-sm sm:text-base" />
                            </a>
                        </div>
                    </div>

                    {/* Column 2 - Quick Links */}
                    <div
                        className={`w-full flex flex-col ${isRTL ? "items-center sm:items-start text-right" : "items-center sm:items-start text-center sm:text-left"}`}
                    >
                        <h3 className="text-lg sm:text-xl font-bold mb-4 sm:mb-6">
                            {t("footer.quickLinks")}
                        </h3>
                        <ul
                            className={`space-y-2 sm:space-y-3 w-full ${isRTL ? "text-center sm:text-right items-center" : ""}`}
                        >
                            <li>
                                <a
                                    href="#home"
                                    className="text-gray-400 hover:text-safir-primary transition-colors duration-300 text-sm sm:text-base"
                                >
                                    {t("footer.home")}
                                </a>
                            </li>
                            <li>
                                <a
                                    href="/courses"
                                    className="text-gray-400 hover:text-safir-primary transition-colors duration-300 text-sm sm:text-base"
                                >
                                    {t("footer.courses")}
                                </a>
                            </li>
                            <li>
                                <a
                                    href="/top-students"
                                    className="text-gray-400 hover:text-safir-primary transition-colors duration-300 text-sm sm:text-base"
                                >
                                    {t("footer.topStudents")}
                                </a>
                            </li>
                            <li>
                                <a
                                    href="/news"
                                    className="text-gray-400 hover:text-safir-primary transition-colors duration-300 text-sm sm:text-base"
                                >
                                    {t("navbar.news")}
                                </a>
                            </li>
                            <li>
                                <a
                                    href="/about"
                                    className="text-gray-400 hover:text-safir-primary transition-colors duration-300 text-sm sm:text-base"
                                >
                                    {t("navbar.about")}
                                </a>
                            </li>
                            <li>
                                <a
                                    href="/donate"
                                    className="text-gray-400 hover:text-safir-primary transition-colors duration-300 text-sm sm:text-base"
                                >
                                    {t("footer.donate")}
                                </a>
                            </li>
                            <li>
                                <a
                                    href="/contact"
                                    className="text-gray-400 hover:text-safir-primary transition-colors duration-300 text-sm sm:text-base"
                                >
                                    {t("footer.contact")}
                                </a>
                            </li>
                            <li>
                                <a
                                    href="/login"
                                    className="text-gray-400 hover:text-safir-primary transition-colors duration-300 text-sm sm:text-base"
                                >
                                    {t("footer.adminLogin")}
                                </a>
                            </li>
                        </ul>
                    </div>

                    {/* Column 3 - Contact Info */}
                    <div
                        className={`w-full flex flex-col ${isRTL ? "text-right items-center sm:items-start" : "text-left items-center sm:items-start"}`}
                    >
                        <h3 className="text-lg sm:text-xl font-bold mb-4 sm:mb-6">
                            {t("footer.contact")}
                        </h3>
                        <ul
                            className={`space-y-3 sm:space-y-4 w-full ${isRTL ? "flex flex-col items-center sm:items-end" : ""}`}
                        >
                            <li
                                className={`flex items-start gap-2 sm:gap-3 ${isRTL ? " text-right justify-start w-full" : "justify-start"}`}
                            >
                                <FaMapMarkerAlt className="text-safir-primary mt-1 flex-shrink-0 w-4 h-4 sm:w-5 sm:h-5" />
                                <span className="text-gray-400 text-xs sm:text-sm leading-relaxed">
                                    {t("footer.footerAddress")}
                                </span>
                            </li>
                            <li
                                className={`flex items-start gap-2 sm:gap-3 ${isRTL ? " text-right justify-start w-full" : "justify-start"}`}
                            >
                                <FaPhoneAlt className="text-safir-primary mt-1 flex-shrink-0 w-4 h-4 sm:w-5 sm:h-5" />
                                <span
                                    className="text-gray-400 text-xs sm:text-sm"
                                    dir="ltr"
                                >
                                    {t("footer.footerPhone")}
                                </span>
                            </li>
                            <li
                                className={`flex items-start gap-2 sm:gap-3 ${isRTL ? " text-right justify-start w-full" : "justify-start"}`}
                            >
                                <FaEnvelope className="text-safir-primary mt-1 flex-shrink-0 w-4 h-4 sm:w-5 sm:h-5" />
                                <span className="text-gray-400 text-xs sm:text-sm break-all">
                                    {t("footer.footerEmail")}
                                </span>
                            </li>
                        </ul>
                    </div>

                    {/* Column 4 - WhatsApp Subscription */}
                    <div
                        className={`w-full flex flex-col ${isRTL ? "items-center sm:items-end text-center sm:text-right" : "items-center sm:items-start text-center sm:text-left"}`}
                    >
                        <h3 className="text-lg sm:text-xl font-bold mb-4 sm:mb-6">
                            {t("footer.newsletter")}
                        </h3>
                        <p
                            className={`text-gray-400 mb-3 sm:mb-4 text-xs sm:text-sm ${isRTL ? "text-right" : "text-center sm:text-left"}`}
                        >
                            {t("footer.newsletterDesc")}
                        </p>
                        <form
                            onSubmit={handleWhatsAppSubmit}
                            className="mb-3 sm:mb-4 w-full max-w-sm"
                        >
                            <div
                                className={`flex items-stretch rounded-lg overflow-hidden border border-safir-primary/60 bg-safir-accent/60 ${isRTL ? "flex-row-reverse" : ""}`}
                            >
                                <input
                                    type="tel"
                                    name="whatsappNumber"
                                    placeholder={t(
                                        "footer.whatsappPlaceholder",
                                    )}
                                    className="bg-transparent text-white px-2 sm:px-3 py-2 sm:py-3 w-full focus:outline-none placeholder-gray-300 text-sm sm:text-base"
                                    pattern="[0-9]{9,15}"
                                    title={t("footer.subscribeError")}
                                    required
                                    dir={isRTL ? "rtl" : "ltr"}
                                />
                                <button
                                    type="submit"
                                    className="bg-safir-primary hover:bg-safir-secondary px-3 sm:px-4 py-2 sm:py-3 flex items-center justify-center transition-colors duration-300"
                                >
                                    <FaWhatsapp className="text-lg sm:text-xl" />
                                </button>
                            </div>
                            {notification && (
                                <div
                                    className={`mt-2 text-xs sm:text-sm p-2 rounded ${notification.type === "success" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}
                                >
                                    {notification.message}
                                </div>
                            )}
                        </form>
                        <p className="text-gray-500 text-xs sm:text-sm text-center sm:text-left">
                            {t("footer.agree")}
                        </p>
                    </div>
                </div>
            </div>

            {/* Bottom Footer */}
            <div className="border-t border-gray-800 py-4 sm:py-6">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row justify-between items-center gap-3 sm:gap-4">
                    <p className="text-gray-400 text-center md:text-left text-xs sm:text-sm">
                        &copy; {currentYear} {t("footer.copyright")}
                    </p>
                    <div className="mt-2 md:mt-0">
                        <ul className="flex flex-wrap justify-center md:justify-start gap-3 sm:gap-4 md:gap-6">
                            <li>
                                <button
                                    type="button"
                                    className="text-gray-400 hover:text-safir-primary text-xs sm:text-sm transition-colors duration-300 bg-none border-none cursor-pointer p-0"
                                    onClick={() =>
                                        handlePolicyClick(
                                            t("footer.privacyPolicy"),
                                        )
                                    }
                                >
                                    {t("footer.privacyPolicy")}
                                </button>
                            </li>
                            <li>
                                <button
                                    type="button"
                                    className="text-gray-400 hover:text-safir-primary text-xs sm:text-sm transition-colors duration-300 bg-none border-none cursor-pointer p-0"
                                    onClick={() =>
                                        handlePolicyClick(
                                            t("footer.termsOfService"),
                                        )
                                    }
                                >
                                    {t("footer.termsOfService")}
                                </button>
                            </li>
                            <li>
                                <button
                                    type="button"
                                    className="text-gray-400 hover:text-safir-primary text-xs sm:text-sm transition-colors duration-300 bg-none border-none cursor-pointer p-0"
                                    onClick={() =>
                                        handlePolicyClick(
                                            t("footer.cookiePolicy"),
                                        )
                                    }
                                >
                                    {t("footer.cookiePolicy")}
                                </button>
                            </li>
                        </ul>
                    </div>
                </div>
                {notification && (
                    <div className="fixed left-1/2 bottom-8 transform -translate-x-1/2 bg-safir-primary text-white px-6 py-3 rounded-md shadow-lg z-50 animate-bounce">
                        {getNotificationText(notification)}
                    </div>
                )}
            </div>
        </footer>
    );
};

export default Footer;
